import { Dispatch } from 'redux';
import { forEach, get, map, omit, range, toNumber, toString } from 'lodash';
import { ReduxStore } from '../payload-types';
import { QUOTE_FORM, CLEAR_QUOTE_FORM, COVERME_TRAVELLER_TYPES } from './types';
import { evaluateAge } from '../travellers-plan/actions';
import { checkTrue, dayjs } from '@trip-ux/common';

export const setQuoteForm = (values, dateFormat?: string) => (dispatch: Dispatch, getState: () => ReduxStore) => {
  const payload = convertQuoteForm(values, dateFormat);
  dispatch({
    type: QUOTE_FORM,
    payload
  });
};

export const convertQuoteForm = (values, dateFormat?: string) => {
  const { travellers, ...rest } = values;
  if (!values.applicationDate) {
    rest.applicationDate = dayjs().format(dateFormat);
  }
  //reset Age if needed
  const ageCalcDate =
    rest.travellerType === COVERME_TRAVELLER_TYPES.VISITOR_TO_CANADA ||
      rest.travellerType === COVERME_TRAVELLER_TYPES.STUDENT
      ? rest.departureDate
      : rest.applicationDate;

  let departureDate = dayjs(rest.departureDate, dateFormat);
  if (rest.travellerType === COVERME_TRAVELLER_TYPES.EXISTING_PLAN) {
    departureDate = dayjs(rest.departureDate, dateFormat).add(rest.planCoverageDayCount, 'day');
  }
  return {
    ...rest,
    ...(dateFormat && {
      coverageDaysGood: departureDate.isAfter(dayjs(rest.applicationDate, dateFormat).subtract(1, 'day')),
      destinationCountry: values.destinationCountry,
      destinationName: values.destinationName,
      hasUSLayover: values.hasUSLayover
    }),
    travellers: map(travellers, (traveller, idx) => {
      const ageDate = ageCalcDate ? dayjs(ageCalcDate, traveller.format) : dayjs();
      return {
        ...traveller,
        age: ageDate.diff(dayjs(traveller.dateOfBirth, traveller.format), 'years'),
        referenceNumber: toNumber(idx) + 1
      };
    })
  };
};

export const clearQuoteForm = () => (dispatch: Dispatch) => {
  dispatch({
    type: CLEAR_QUOTE_FORM
  });
};

export const setCoverMeQuoteForm = payload => (dispatch: Dispatch) => {
  dispatch({
    type: QUOTE_FORM,
    payload
  });
};

export const convertCoverMeQuoteForm = (
  values,
  dateFormat: string,
  isTravellerTypeChanged: boolean,
  organizationCategory
) => {
  const isStudent = values.travellerType === COVERME_TRAVELLER_TYPES.STUDENT;
  const isTopUp = values.travellerType === COVERME_TRAVELLER_TYPES.EXISTING_PLAN;
  const isVTC = values.travellerType === COVERME_TRAVELLER_TYPES.VISITOR_TO_CANADA;
  const primaryTravellerPaxId = 1;
  const travellerCount = values.travellerCount ? values.travellerCount : 1;
  const travellers = map(range(travellerCount), index => {
    const paxId = index + 1;
    const tripBookingDate = get(values, `travellers[${index}].tripBookingDate`, dayjs().format(dateFormat));
    const coverageStartDate = get(values, `coverageStartDate`, dayjs().format(dateFormat));
    const departureDate = get(values, `departureDate`, dayjs().format(dateFormat));
    const dob = get(values, `travellers[${index}].dateOfBirth`);
    const ageCalcDate = isVTC ? coverageStartDate : isStudent ? departureDate : tripBookingDate;
    const { age, dateOfBirth } = evaluateAge({
      dateOfBirth: get(values, `dob${paxId}`, dob),
      format: dateFormat,
      paxId,
      ageCalcDate
    });
    const tripCost = get(values, `tripCostSameAsPrimary${paxId}`)
      ? get(values, `tripCost${primaryTravellerPaxId}`)
      : get(values, `tripCost${paxId}`);
    const isAgency = organizationCategory === 'AGENCY' || 'BROKERAGE';

    return {
      ...get(values, `travellers[${index}]`),
      ...(!isAgency ? { tripBookingDate } : {}),
      age,
      dateOfBirth,
      format: dateFormat,
      referenceNumber: paxId,
      ...(isAgency ? { tripCost } : {})
    };
  });

  const isFamily = (): boolean => {
    if (isStudent && values.familyConfirmation) {
      return values.familyConfirmation;
    } else if (!isStudent && checkTrue(values.travellerDependentQn)) {
      return values.dependentWaiver;
    } else {
      return false;
    }
  };

  let data = {};

  if (!isTravellerTypeChanged) {
    data = omit(values, [
      'familyConfirmation',
      'email1',
      'phone1',
      'travellerDependentQn',
      'dependentWaiver',
      'studentType',
      'planCoverageDayCount'
    ]);
    forEach(range(values.travellerCount), index => {
      const paxId = index + 1;
      data = omit(data, [`firstName${paxId}`, `lastName${paxId}`, `dob${paxId}`, `tripCost${paxId}`]);
    });
  }

  if (isStudent) {
    data = { ...data, studentType: values.studentType };
  }
  if (isTopUp) {
    data = { ...data, planCoverageDayCount: values.planCoverageDayCount };
  }

  data = {
    ...data,
    travellerType: values.travellerType,
    //travellerCount: values.travellerCount,
    travellerCount: travellerCount,
    country: 'CA',
    familyPlans: toString(isFamily()),
    isFamily: isFamily(),
    province: get(values, 'province'),
    travellers
  };
  return data;
};
